import { MouseEvent, useEffect, useState } from 'react';

import { useKeenSlider } from 'keen-slider/react';

import { RevenuesTopMyAssetsResponseDto } from 'services/hooks/revenue';

import { useRankContext } from './useRankContext';

const SLIDE_ITEM_WIDTH = 150; // 슬라이드 하나의 고정된 너비

export default function useRankingSlider(data: RevenuesTopMyAssetsResponseDto[]) {
  const { assetScope } = useRankContext();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    created() {
      setLoaded(true);
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    drag: true,
    loop: false,
    mode: 'free-snap',
    rubberband: false,
    slides: {
      perView: 'auto',
      spacing: 0,
    },
    defaultAnimation: {
      duration: 700,
    },
  });

  // 좌우 이동 이벤트 핸들러
  const translateLeft = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // instanceRef.current?.prev();
    if (instanceRef.current) {
      const sliderWidth = instanceRef.current.size;
      const slidesPerView = Math.floor(sliderWidth / SLIDE_ITEM_WIDTH); // 현재 표시되는 슬라이드 개수
      const targetSlide = Math.max(currentSlide - slidesPerView, 0);
      instanceRef.current.moveToIdx(targetSlide);
      setCurrentSlide(targetSlide);
    }
  };

  const translateRight = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // instanceRef.current?.next();
    if (instanceRef.current) {
      const sliderWidth = instanceRef.current.size;
      const slidesPerView = Math.floor(sliderWidth / SLIDE_ITEM_WIDTH); // 현재 표시되는 슬라이드 개수
      const targetSlide = Math.min(currentSlide + slidesPerView, instanceRef.current.track.details.maxIdx);
      instanceRef.current.moveToIdx(targetSlide);
      setCurrentSlide(targetSlide);
    }
  };

  // Desktop 화면에서 화살표를 언제 보여주는지 여부 판단 로직 (mobile은 안보여줌)
  const [isExceedContainerWidth, setIsExceedContainerWidth] = useState(false);

  useEffect(() => {
    if (data && instanceRef.current) {
      instanceRef.current.update();
      // 슬라이더의 전체 너비와 컨테이너 너비 비교해서 더 넓은 경우에만 화살표 표시
      const sliderWidth = instanceRef.current.size;
      const totalSlideWidth = data.length * SLIDE_ITEM_WIDTH;
      setIsExceedContainerWidth(sliderWidth < totalSlideWidth);
    }
  }, [data, instanceRef]);

  // 탭 변경 시, 슬라이더 위치 초기화
  useEffect(() => {
    if (!data.length) return;

    const resetSlider = () => {
      if (!instanceRef.current) return;
      // 슬라이더 초기화
      instanceRef.current.moveToIdx(0);
      setCurrentSlide(0);
      instanceRef.current.update();
    };

    resetSlider();
  }, [assetScope, data, instanceRef]);

  useEffect(() => {
    // 양쪽 화살표의 너비 유무가 동적으로 계산되므로, isExceedContainerWidth 상태를 활용하여 재업데이트 시도
    instanceRef.current?.update();
  }, [instanceRef, isExceedContainerWidth]);

  return {
    currentSlide,
    translateLeft,
    translateRight,
    instanceRef,
    sliderRef,
    isExceedContainerWidth,
    loaded,
    rankList: data,
  };
}
